/**
 * Tells the captain, once, that they were fined for leaving a battle.
 *
 * The server owns the penalty and reports it on `GET /api/progression`
 * (`desertion`, unseen only); this file owns nothing but the telling. It is
 * called from the API client rather than from a screen so every screen that
 * reads the overview tells it the same way, and the harbour HUD stays out of it.
 * `net/api.ts` is imported as a type only — the call goes the other way.
 */
import { useI18n } from '@/i18n';
import type { DesertionNotice } from '@/net/api';
import { pushSlip } from './slips';

/** Ids already pinned this tab, so two screens reading the overview at once say it once. */
const told = new Set<string>();

export function announceDesertion(notice: DesertionNotice | null | undefined, acknowledge: (id: string) => Promise<unknown>): void {
  if (!notice || told.has(notice.id)) return;
  told.add(notice.id);
  const { t, n } = useI18n();
  pushSlip({
    tone: 'error',
    text: t('slip.desertion.title'),
    detail: t('slip.desertion.detail', { rating: n(notice.ratingLost), credits: n(notice.creditsFined) }),
  });
  // Acknowledged on show, not on read: a failed ack only means the slip comes
  // back next visit, which is better than a fine nobody saw.
  void acknowledge(notice.id).catch(() => told.delete(notice.id));
}

/** Tests: forget what was told. */
export function resetDesertionNotices(): void {
  told.clear();
}
